import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * Custom element for K-Way color-variant swatches.
 *
 * On product pages, clicking a pill replaces the current history entry with the new product URL,
 *
 * When `data-enable-card-refresh="true"` is set (card context only), clicking a pill
 * fetches the Featured Product Card section for the new product and morphs the card
 * in place, without navigating away.
 *
 * Pills inside `product-recommendations` or `dialog-component.search-modal`
 * are intentionally excluded and keep their default navigation behaviour.
 */
class KwaySwatches extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {AbortController | null} */
  #fetchAbortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const enableCardRefresh = this.dataset.enableCardRefresh === 'true';
    if (!enableCardRefresh) {
      this.addEventListener('click', this.#handleClick, {
        signal: this.#abortController.signal,
      });
    } else {
      const productUrl = this.dataset.productUrl;
      if (!productUrl) return;
      this.addEventListener(
        'click',
        () => {
          if (this.classList.contains('kway-swatches__pill--selected')) return;
          this.refreshCard(productUrl);
        },
        {
          signal: this.#abortController.signal,
        },
      );
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#fetchAbortController?.abort();
    this.#fetchAbortController = null;
  }

  /**
   * On PDP pages it intercepts clicks on `.kway-swatches__pill` anchors and calls
   * `location.replace()` so that pressing "back" returns to the PLP instead of
   * cycling through every visited color variant.
   * @param {MouseEvent} event
   * */
  #handleClick = (event) => {
    const productUrl = this.dataset.productUrl;
    if (!productUrl) return;

    // PDP: intercept and replace history entry so "back" skips color variants.
    if (this.closest('[id*="ProductInformation-"]')) {
      event.preventDefault();
      location.replace(productUrl);
      return;
    }

    // Card with refresh enabled: fetch and morph the Featured Product Card section.
    if (this.dataset.enableCardRefresh === 'true') {
      event.preventDefault();
      this.refreshCard(productUrl);
    }
  };

  /**
   * Fetches the section-rendering-product-card section for the given product URL
   * and morphs the relevant card elements (price, badges, product features, GTM data) in place.
   * @param {string} pillHref - The href of the clicked swatch pill (target product URL).
   */
  refreshCard(pillHref) {
    const productCard = this.closest('product-card');
    if (!productCard) return;

    // Update selected state on pills
    const pillsContainer = this.closest('.kway-swatches__pills');
    /** @type {Element | null} */
    let previousSelected = null;
    if (pillsContainer) {
      previousSelected = pillsContainer.querySelector('.kway-swatches__pill--selected');
      if (previousSelected) {
        previousSelected.classList.remove('kway-swatches__pill--selected');
      }
    }
    this.classList.add('kway-swatches__pill--selected');

    const productPath = new URL(pillHref, window.location.origin).pathname;
    const requestUrl = `${productPath}?section_id=section-rendering-product-card`;

    this.#fetchAbortController?.abort();
    this.#fetchAbortController = new AbortController();

    fetch(requestUrl, { signal: this.#fetchAbortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        const newProductCard = html.querySelector('product-card');
        if (!newProductCard) throw new Error('Fetched content does not contain a product card');

        /** @type {Array<[string, string]>} */
        const elementSelectors = [
          ['.card-gallery', '.card-gallery'],
          ['product-price', 'product-price'],
          ['.product-features', '.product-features'],
        ];

        for (const [currentSelector, newSelector] of elementSelectors) {
          const currentEl = productCard.querySelector(currentSelector);
          const newEl = newProductCard.querySelector(newSelector);
          if (currentEl && newEl) {
            morph(currentEl, newEl);
          }
        }

        // Update GTM data attributes on the product-card element
        for (const attr of newProductCard.attributes) {
          if (attr.name.startsWith('data-gtm-') || attr.name === 'data-is-gtm-item') {
            productCard.setAttribute(attr.name, attr.value);
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
          this.#revertSelectedState(previousSelected);
        }
      });
  }

  /**
   * Reverts the selected pill state to the previous one.
   * @param {Element | null} previousSelected - The previously selected pill element.
   */
  #revertSelectedState(previousSelected) {
    this.classList.remove('kway-swatches__pill--selected');
    if (previousSelected) {
      previousSelected.classList.add('kway-swatches__pill--selected');
    }
  }
}

if (!customElements.get('kway-swatches')) {
  customElements.define('kway-swatches', KwaySwatches);
}
